import React, { useCallback, useEffect, useRef, useState } from "react";
import AsyncSelect from "react-select/async";
import { getReq } from "../utils/request";

export interface UserPickerValue {
    userId: number;
    /** Profile id — populated when the BE returns it (e.g. /users/search/simple).
     *  Required for callers that operate on UserDetail (operator-shift
     *  start-on-behalf, principal pickers). May be null for endpoints
     *  that only project userId. */
    userDetailId?: number | null;
    firstName: string;
    lastName: string | null;
    fullName: string;
    photo?: string | null;
}

interface UserOption {
    value: number;
    label: string;
    raw: UserPickerValue;
}

interface UserPickerProps {
    label?: string;
    id?: string;
    value: UserPickerValue | null;
    onChange: (user: UserPickerValue | null) => void;
    placeholder?: string;
    disabled?: boolean;
    isClearable?: boolean;
    required?: boolean;
    /**
     * Optional tooltip text. When provided, an info icon is rendered
     * absolutely positioned over the picker (matching the previous
     * assignedUser callsites in Lead/Mission/MissionRequest).
     */
    tooltip?: string;
    /**
     * Scope the BE search to one or more provider/client orgs via the
     * `organizationIds` query param. Used by mission team-roster
     * pickers; harmless for unrestricted dropdowns.
     */
    organizationId?: number | null;
    organizationIds?: number[];
}

const toUserPickerValue = (u: any): UserPickerValue | null => {
    const userId = u?.userId ?? null;
    if (!userId) return null;
    const firstName: string = u?.firstName ?? u?.userDetail?.firstName ?? "";
    const lastName: string | null =
        u?.lastName ?? u?.userDetail?.lastName ?? null;
    const fullName: string =
        (u?.fullName && String(u.fullName).trim()) ||
        `${firstName} ${lastName ?? ""}`.trim();
    return {
        userId,
        userDetailId: u?.userDetailId ?? u?.userDetail?.userDetailId ?? null,
        firstName,
        lastName,
        fullName,
        photo: u?.photo ?? u?.userDetail?.photo ?? null,
    };
};

const toOption = (u: UserPickerValue): UserOption => ({
    value: u.userId,
    label: u.fullName || `${u.firstName} ${u.lastName ?? ""}`.trim(),
    raw: u,
});

const UserPicker: React.FC<UserPickerProps> = ({
    label,
    id,
    value,
    onChange,
    placeholder,
    disabled = false,
    isClearable = true,
    required = false,
    tooltip,
    organizationId,
    organizationIds,
}) => {
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const [selected, setSelected] = useState<UserOption | null>(
        value ? toOption(value) : null,
    );

    useEffect(() => {
        setSelected(value ? toOption(value) : null);
    }, [value]);

    // Build the org-scope CSV from either single-id or array forms.
    // Memoized so the fetcher reference is stable per filter change
    // (the AsyncSelect debounce relies on a stable identity).
    const orgScopeCsv = (() => {
        const ids: number[] = [];
        if (organizationId != null && Number.isFinite(organizationId)) ids.push(Number(organizationId));
        if (Array.isArray(organizationIds)) {
            for (const o of organizationIds) {
                if (o != null && Number.isFinite(o)) ids.push(Number(o));
            }
        }
        return ids.length > 0 ? ids.join(",") : "";
    })();

    const fetchUsers = useCallback(async (keyword: string): Promise<UserOption[]> => {
        try {
            const kw = encodeURIComponent(keyword ?? "");
            const orgQs = orgScopeCsv ? `&organizationIds=${encodeURIComponent(orgScopeCsv)}` : "";
            const response = await getReq(`users/search/simple?keyword=${kw}${orgQs}`);
            if (response?.status !== 200) return [];
            const body = response.body;
            const list: any[] = Array.isArray(body?.data)
                ? body.data
                : Array.isArray(body)
                    ? body
                    : [];
            return list
                .map(toUserPickerValue)
                .filter((u): u is UserPickerValue => u !== null)
                .map(toOption);
        } catch {
            return [];
        }
    }, [orgScopeCsv]);

    const loadOptions = useCallback(
        (inputValue: string, callback: (options: UserOption[]) => void) => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            debounceRef.current = setTimeout(() => {
                fetchUsers(inputValue).then(callback);
            }, 300);
        },
        [fetchUsers],
    );

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    const handleChange = (option: UserOption | null) => {
        setSelected(option);
        onChange(option ? option.raw : null);
    };

    // Single placeholder carries the label text (e.g. "Assignee") to avoid
    // overlap with the form-floating label used by other Input components.
    const effectivePlaceholder =
        placeholder ?? (label ? `${label}${required ? " *" : ""}` : "Search user by name...");

    const picker = (
        <AsyncSelect<UserOption>
            inputId={id}
            value={selected}
            loadOptions={loadOptions}
            onChange={(opt) => handleChange(opt as UserOption | null)}
            defaultOptions
            cacheOptions
            placeholder={effectivePlaceholder}
            isDisabled={disabled}
            isClearable={isClearable}
            className="custom-select"
            classNamePrefix="custom-select"
            noOptionsMessage={({ inputValue }) =>
                inputValue.trim() === ""
                    ? "Start typing to search users..."
                    : "No users found"
            }
            aria-label={label}
            aria-required={required}
        />
    );

    if (!tooltip) return picker;

    // Tooltip wrapper — info-icon floats over the right edge of the
    // picker, shifted left when a clear-`x` is showing to avoid the
    // chevron+clear cluster. Matches the legacy Lead/Mission/MR
    // assignedUser layout exactly.
    return (
        <div style={{ position: "relative" }}>
            {picker}
            <i
                className="bi bi-info-circle"
                title={tooltip}
                style={{
                    position: "absolute",
                    top: 6,
                    right: value && isClearable ? 52 : 32,
                    fontSize: "var(--fs-md)",
                    pointerEvents: "auto",
                    cursor: "help",
                    zIndex: 1,
                }}
            />
        </div>
    );
};

export default UserPicker;
